import React from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiClock, FiCheckCircle, FiXCircle, FiMail, FiArrowLeft, FiMusic } = FiIcons;

const STATUS_CONFIG = {
  pending: {
    icon: FiClock,
    iconColor: 'text-amber-400',
    iconBg: 'bg-amber-500/10 border-amber-500/20',
    title: 'Application Under Review',
    subtitle: 'Our team is verifying your submission. This usually takes 24–48 hours.',
    badge: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
    badgeText: 'Pending Review',
  },
  approved: {
    icon: FiCheckCircle,
    iconColor: 'text-green-400',
    iconBg: 'bg-green-500/10 border-green-500/20',
    title: 'Application Approved!',
    subtitle: 'Welcome to Vision Direct. Your profile is now active.',
    badge: 'bg-green-500/10 border-green-500/30 text-green-400',
    badgeText: 'Approved',
  },
  rejected: {
    icon: FiXCircle,
    iconColor: 'text-red-400',
    iconBg: 'bg-red-500/10 border-red-500/20',
    title: 'Application Not Approved',
    subtitle: 'Unfortunately we could not verify your submission at this time.',
    badge: 'bg-red-500/10 border-red-500/30 text-red-400',
    badgeText: 'Not Approved',
  },
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="text-[10px] font-black uppercase tracking-widest text-zinc-600">{label}</span>
    <span className="text-xs font-black text-zinc-300 truncate max-w-[60%] text-right">{value}</span>
  </div>
);

const PendingApprovalScreen = ({ request, onSignOut }) => {
  const cfg = STATUS_CONFIG[request?.status] || STATUS_CONFIG.pending;
  const role = request?.requested_role || 'artist';

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-brand-pink/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-1/3 w-96 h-96 bg-brand-coral/5 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-lg relative z-10 space-y-8">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors">
            <SafeIcon icon={FiArrowLeft} className="text-lg" />
            <span className="text-xs font-black uppercase tracking-widest">Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <img
              src="https://media-manager-c.questera.ai/greta-media/5fb2f9d5255c375c4c593c40a3604cfd971c9f56c7725c3a05c28f383b843f2c3e4f4687d5d18e728c639d20e7900999/images/aW1hZ2UvcG5n/e291f38d345a3c31aa9db040447e5035.png"
              alt="Vision Direct Logo"
              className="h-7 w-7 object-contain drop-shadow-[0_0_8px_rgba(255,102,204,0.4)]"
            />
            <span className="text-2xl font-black tracking-tighter text-white uppercase italic">Vision Direct</span>
          </div>
        </div>

        <div className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800 rounded-[2.5rem] p-10 space-y-8">
          <div className="flex flex-col items-center text-center space-y-5">
            <div className={`w-20 h-20 rounded-3xl border-2 flex items-center justify-center ${cfg.iconBg}`}>
              <SafeIcon icon={cfg.icon} className={`text-4xl ${cfg.iconColor}`} />
            </div>
            <span className={`px-4 py-1.5 rounded-full border text-[10px] font-black uppercase tracking-widest ${cfg.badge}`}>
              {cfg.badgeText}
            </span>
            <div className="space-y-2">
              <h1 className="text-3xl font-black tracking-tighter text-white uppercase leading-tight">{cfg.title}</h1>
              <p className="text-zinc-400 font-bold text-sm leading-relaxed">{cfg.subtitle}</p>
            </div>
          </div>

          {request && (
            <div className="space-y-3 p-5 bg-zinc-950/60 rounded-2xl border border-zinc-800">
              <p className="text-[10px] font-black uppercase tracking-widest text-zinc-600">Your Submission</p>
              <div className="space-y-2">
                <Row label="Name"      value={request.display_name} />
                <Row label="Role"      value={request.requested_role.charAt(0).toUpperCase() + request.requested_role.slice(1)} />
                <Row label="Submitted" value={new Date(request.created_at).toLocaleDateString()} />
                {request.social_instagram && <Row label="Instagram" value={request.social_instagram} />}
                {request.social_spotify   && <Row label="Spotify"   value={request.social_spotify} />}
              </div>
            </div>
          )}

          {request?.status === 'rejected' && request?.reviewer_notes && (
            <div className="p-5 bg-red-500/5 border border-red-500/20 rounded-2xl space-y-2">
              <p className="text-[10px] font-black uppercase tracking-widest text-red-400">Feedback</p>
              <p className="text-zinc-300 text-sm font-bold leading-relaxed">{request.reviewer_notes}</p>
            </div>
          )}

          {request?.status === 'pending' && (
            <div className="space-y-3">
              <p className="text-[10px] font-black uppercase tracking-widest text-zinc-600">What Happens Next</p>
              {[
                { icon: FiMail,  text: `We'll email you at ${request?.email} when your application is reviewed.` },
                { icon: FiMusic, text: `Once approved, your ${role} portal will go live immediately.` },
              ].map(({ icon, text }, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-xl bg-zinc-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <SafeIcon icon={icon} className="text-zinc-400 text-xs" />
                  </div>
                  <p className="text-zinc-400 text-xs font-bold leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-col gap-3 pt-2">
            {request?.status === 'approved' && (
              <Link to="/"
                className="w-full py-4 btn-brand text-xs rounded-2xl text-center hover:scale-[1.02] transition-all">
                Go to My Portal →
              </Link>
            )}
            <button onClick={onSignOut}
              className="w-full py-4 border border-zinc-800 text-zinc-500 font-black uppercase tracking-widest text-xs rounded-2xl hover:border-zinc-600 hover:text-zinc-300 transition-all">
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PendingApprovalScreen;